
/* =========================================================
   src/pages/posts.js
   Updated:
   - Cloudinary video loading removed
   - Firebase Storage used for new video uploads
   - ImgBB used for image uploads
   - Old Cloudinary videos are skipped
========================================================= */

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "../context/AuthContext";

import {
  addPost,
  addVideoPost,
  getPostsFeed,
  getVideosFeed,
  getUserSettings,
  toggleLike,
  toggleVideoLike,
  uploadVideoToFirebase,
  db,
} from "../firebase";

import "../styles/posts.css";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  AiOutlineCamera,
  AiOutlineVideoCamera,
  AiOutlineCloseCircle,
  AiOutlineEnvironment,
  AiOutlineLink,
} from "react-icons/ai";

import { FaUserCircle } from "react-icons/fa";

import {
  collection,
  getDocs,
} from "firebase/firestore";

import { Helmet } from "react-helmet";

/* =========================================================
   AREAS
========================================================= */

const AREAS = [
  "All",
  "Tiruchengode",
  "Sankagiri",
  "Kumarapalayam",
  "Pallipalayam",
];

/* =========================================================
   CHECK OLD CLOUDINARY URL
========================================================= */

const isCloudinaryUrl = (url = "") => {
  if (!url || typeof url !== "string") {
    return false;
  }

  return (
    url.includes("res.cloudinary.com") ||
    url.includes("cloudinary.com") ||
    url.includes("cloudinary")
  );
};

/* =========================================================
   CHECK VALID URL
========================================================= */

const normalizeUrl = (value = "") => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "";
  }

  if (
    trimmed.startsWith("javascript:") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("vbscript:")
  ) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

const isValidUrl = (value = "") => {
  try {
    const parsed = new URL(value);

    return (
      parsed.protocol === "http:" ||
      parsed.protocol === "https:"
    );
  } catch {
    return false;
  }
};

const getHostname = (value = "") => {
  try {
    return new URL(value)
      .hostname
      .replace(/^www\./, "");
  } catch {
    return "Link";
  }
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function Home() {
  const { user } = useAuth();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  /* =======================================================
     FEED STATES
  ======================================================= */

  const [posts, setPosts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [mergedFeed, setMergedFeed] = useState([]);

  const [loadingFeed, setLoadingFeed] = useState(true);

  const [activeArea, setActiveArea] =
    useState("All updates");

  /* =======================================================
     USER STATES
  ======================================================= */

  const [userPhoto, setUserPhoto] = useState("");

  /* =======================================================
     COMPOSER STATES
  ======================================================= */

  const [text, setText] = useState("");

  const [file, setFile] = useState(null);

  const [preview, setPreview] = useState(null);

  const [fileType, setFileType] = useState("");

  const [posting, setPosting] = useState(false);

  const [uploadProgress, setUploadProgress] =
    useState(0);

  /* =======================================================
     LOCATION STATES
  ======================================================= */

  const [selectedArea, setSelectedArea] =
    useState("");

  const [showLocationPicker, setShowLocationPicker] =
    useState(false);

  /* =======================================================
     URL STATES
  ======================================================= */

  const [urlLink, setUrlLink] = useState("");

  const [showAttachUrlModal, setShowAttachUrlModal] =
    useState(false);

  const [tempUrlInput, setTempUrlInput] =
    useState("");

  const [showUrlPreviewModal, setShowUrlPreviewModal] =
    useState(false);

  const [currentModalUrl, setCurrentModalUrl] =
    useState("");

/* =========================================================
   FULL IMAGE VIEWER
========================================================= */

const [showImageViewer, setShowImageViewer] =
  useState(false);

const [selectedImage, setSelectedImage] =
  useState("");

  /* =======================================================
     PRODUCT SUGGESTIONS
  ======================================================= */

  const [suggestions, setSuggestions] = useState([]);

  /* =========================================================
     LOAD USER AND FEED
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const loadFeed = async () => {
      setLoadingFeed(true);

      try {
        if (user?.uid) {
          const settings = await getUserSettings(
            user.uid
          );

          if (mounted) {
            setUserPhoto(
              settings?.photoURL ||
                user?.photoURL ||
                ""
            );
          }
        }

        const [
          feedPosts,
          feedVideos,
        ] = await Promise.all([
          getPostsFeed(),
          getVideosFeed(),
        ]);

        /*
          Important:
          Old video documents may still contain Cloudinary URLs.
          Remove them before rendering.
        */

        const safePosts = Array.isArray(feedPosts)
          ? feedPosts
          : [];

        const safeVideos = Array.isArray(feedVideos)
          ? feedVideos.filter(
              (item) =>
                item?.videoUrl &&
                !isCloudinaryUrl(item.videoUrl)
            )
          : [];

        if (mounted) {
          setPosts(safePosts);
          setVideos(safeVideos);
        }
      } catch (error) {
        console.error(
          "Failed to load posts feed:",
          error
        );
      } finally {
        if (mounted) {
          setLoadingFeed(false);
        }
      }
    };

    loadFeed();

    return () => {
      mounted = false;
    };
  }, [user]);

  /* =========================================================
     MERGE POSTS AND VIDEOS
  ========================================================= */

  useEffect(() => {
    const combined = [
      ...posts,
      ...videos,
    ].sort((a, b) => {
      const dateA = new Date(
        a?.createdAt || 0
      ).getTime();

      const dateB = new Date(
        b?.createdAt || 0
      ).getTime();

      return dateB - dateA;
    });

    setMergedFeed(combined);
  }, [posts, videos]);

  /* =========================================================
     OPEN SHARED POST
  ========================================================= */

  useEffect(() => {
    const postId = searchParams.get("post");

    if (!postId || mergedFeed.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      const element = document.getElementById(
        `post-${postId}`
      );

      if (!element) {
        return;
      }

      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      element.classList.add(
        "highlight-post"
      );

      setTimeout(() => {
        element.classList.remove(
          "highlight-post"
        );
      }, 3000);
    }, 500);

    return () => clearTimeout(timer);
  }, [mergedFeed, searchParams]);

  /* =========================================================
     LOAD PRODUCT SUGGESTIONS
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const loadSuggestions = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, "products")
        );

        const list = snapshot.docs.map((doc) => ({
          product_id: doc.id,
          ...doc.data(),
        }));

        const shuffled = [...list].sort(
          () => 0.5 - Math.random()
        );

        const picked = shuffled.slice(0, 6);

        if (mounted) {
          setSuggestions(picked);
        }

        window.homeSuggestions = picked;
      } catch (error) {
        console.error(
          "Failed to load product suggestions:",
          error
        );
      }
    };

    loadSuggestions();

    return () => {
      mounted = false;
    };
  }, []);

  /* =========================================================
     AREA FILTER
  ========================================================= */

  const filteredFeed = useMemo(() => {
    if (activeArea === "All updates") {
      return mergedFeed;
    }

    return mergedFeed.filter(
      (item) => item?.area === activeArea
    );
  }, [
    mergedFeed,
    activeArea,
  ]);

  /* =========================================================
     IMG BB IMAGE UPLOAD
  ========================================================= */

  const uploadToImgBB = async (
    selectedFile
  ) => {
    const apiKey =
      process.env.REACT_APP_IMGBB_KEY;

    if (!selectedFile) {
      throw new Error(
        "No image selected."
      );
    }

    if (!apiKey) {
      throw new Error(
        "ImgBB API key is missing. Add REACT_APP_IMGBB_KEY to your .env file."
      );
    }

    if (
      !selectedFile.type.startsWith(
        "image/"
      )
    ) {
      throw new Error(
        "Please select a valid image file."
      );
    }

    const maxSize =
      32 * 1024 * 1024;

    if (selectedFile.size > maxSize) {
      throw new Error(
        "Image must be smaller than 32 MB."
      );
    }

    const formData = new FormData();

    formData.append(
      "image",
      selectedFile
    );

    const response = await fetch(
      `https://api.imgbb.com/1/upload?key=${apiKey}`,
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await response.json();

    console.log(
      "ImgBB response:",
      data
    );

    if (
      !response.ok ||
      !data?.success ||
      !data?.data?.url
    ) {
      throw new Error(
        data?.error?.message ||
          "ImgBB image upload failed."
      );
    }

    return data.data.url;
  };


  /* =========================================================
     URL MODAL FUNCTIONS
  ========================================================= */

  const openAttachUrlModal = () => {
    setTempUrlInput(urlLink);
    setShowAttachUrlModal(true);
  };

  const attachUrl = () => {
    const normalizedUrl =
      normalizeUrl(tempUrlInput);

    if (
      !normalizedUrl ||
      !isValidUrl(normalizedUrl)
    ) {
      alert(
        "Please enter a valid http or https URL."
      );
      return;
    }

    setUrlLink(normalizedUrl);
    setShowAttachUrlModal(false);
  };

  const clearUrl = () => {
    setUrlLink("");
    setTempUrlInput("");
  };

  const openUrlPreviewModal = (url) => {
    if (
      !url ||
      !isValidUrl(url)
    ) {
      alert("Invalid URL.");
      return;
    }

    setCurrentModalUrl(url);
    setShowUrlPreviewModal(true);
  };

  const closeUrlPreviewModal = () => {
    setShowUrlPreviewModal(false);
    setCurrentModalUrl("");
  };

  /* =========================================================
   IMAGE VIEWER FUNCTIONS
========================================================= */

const openImageViewer = (imageUrl) => {
  if (!imageUrl) return;

  setSelectedImage(imageUrl);
  setShowImageViewer(true);
};

const closeImageViewer = () => {
  setShowImageViewer(false);
  setSelectedImage("");
};

  /* =========================================================
     FILE SELECTION
  ========================================================= */

  const handleFileChange = (
    event,
    type
  ) => {
    const selectedFile =
      event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (
      type === "image" &&
      !selectedFile.type.startsWith(
        "image/"
      )
    ) {
      alert(
        "Please select a valid image."
      );
      return;
    }

    if (
      type === "video" &&
      !selectedFile.type.startsWith(
        "video/"
      )
    ) {
      alert(
        "Please select a valid video."
      );
      return;
    }

    if (
      type === "image" &&
      selectedFile.size >
        32 * 1024 * 1024
    ) {
      alert(
        "Image must be smaller than 32 MB."
      );
      return;
    }

    if (
      type === "video" &&
      selectedFile.size >
        100 * 1024 * 1024
    ) {
      alert(
        "Video must be smaller than 100 MB."
      );
      return;
    }

    if (preview) {
      URL.revokeObjectURL(preview);
    }

    const objectUrl =
      URL.createObjectURL(
        selectedFile
      );

    setFile(selectedFile);
    setFileType(type);
    setPreview(objectUrl);
  };

  const removePreview = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }

    setFile(null);
    setPreview(null);
    setFileType("");
    setUploadProgress(0);
  };

  /* =========================================================
     REFRESH FEED
  ========================================================= */

  const refreshFeed = async () => {
    try {
      const [
        latestPosts,
        latestVideos,
      ] = await Promise.all([
        getPostsFeed(),
        getVideosFeed(),
      ]);

      const safePosts = Array.isArray(
        latestPosts
      )
        ? latestPosts
        : [];

      const safeVideos = Array.isArray(
        latestVideos
      )
        ? latestVideos.filter(
            (item) =>
              item?.videoUrl &&
              !isCloudinaryUrl(
                item.videoUrl
              )
          )
        : [];

      setPosts(safePosts);
      setVideos(safeVideos);
    } catch (error) {
      console.error(
        "Failed to refresh feed:",
        error
      );
    }
  };

  /* =========================================================
     HANDLE POST
  ========================================================= */

  const handlePost = async () => {
    if (
      !text.trim() &&
      !file &&
      !urlLink
    ) {
      alert(
        "Write something or add media or URL."
      );
      return;
    }

    if (!selectedArea) {
      setShowLocationPicker(true);
      return;
    }

    if (!user?.uid) {
      alert(
        "Please login to post."
      );
      return;
    }

    if (posting) {
      return;
    }

    setPosting(true);
    setUploadProgress(0);

    try {
      const settings =
        await getUserSettings(
          user.uid
        );

      const userName =
        settings?.name ||
        user?.displayName ||
        "User";

      const photoURL =
        settings?.photoURL ||
        userPhoto ||
        user?.photoURL ||
        "";

      /* =====================================================
         FIREBASE VIDEO UPLOAD
      ===================================================== */

      if (fileType === "video") {
        if (!file) {
          throw new Error(
            "No video selected."
          );
        }

        console.log(
          "Starting Firebase Storage video upload..."
        );

        const uploadResult =
          await uploadVideoToFirebase(
            file,
            user.uid,
            (progress) => {
              setUploadProgress(
                progress
              );
            }
          );

        console.log(
          "Firebase video upload result:",
          uploadResult
        );

        const videoUrl =
          uploadResult?.videoUrl;

        const storagePath =
          uploadResult?.storagePath ||
          "";

        if (!videoUrl) {
          throw new Error(
            "Firebase did not return a video URL."
          );
        }

        /*
          Safety check:
          Never save a Cloudinary video URL.
        */

        if (
          isCloudinaryUrl(videoUrl)
        ) {
          throw new Error(
            "Cloudinary URL detected. Video was not saved."
          );
        }

        await addVideoPost(
          user.uid,
          text.trim(),
          videoUrl,
          selectedArea,
          userName,
          photoURL,
          storagePath,
          urlLink
        );
      }

      /* =====================================================
         IMAGE UPLOAD
      ===================================================== */

      else if (
        fileType === "image"
      ) {
        if (!file) {
          throw new Error(
            "No image selected."
          );
        }

        const imageUrl =
          await uploadToImgBB(
            file
          );

        if (!imageUrl) {
          throw new Error(
            "Image upload failed."
          );
        }

        await addPost(
          user.uid,
          text.trim(),
          imageUrl,
          selectedArea,
          userName,
          photoURL,
          urlLink
        );
      }

      /* =====================================================
         TEXT / URL POST
      ===================================================== */

      else {
        await addPost(
          user.uid,
          text.trim(),
          "",
          selectedArea,
          userName,
          photoURL,
          urlLink
        );
      }

      /* =====================================================
         CLEAR COMPOSER
      ===================================================== */

      if (preview) {
        URL.revokeObjectURL(preview);
      }

      setText("");
      setFile(null);
      setPreview(null);
      setFileType("");
      setUrlLink("");
      setTempUrlInput("");
      setSelectedArea("");
      setUploadProgress(0);

      await refreshFeed();

      alert(
        "Posted successfully!"
      );
    } catch (error) {
      console.error(
        "POST ERROR:",
        error
      );

      alert(
        error?.message ||
          "Failed to post. Please try again."
      );
    } finally {
      setPosting(false);
      setUploadProgress(0);
    }
  };

  /* =========================================================
     HANDLE LIKE
  ========================================================= */

  const handleLike = async (
    item
  ) => {
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const likes =
        Array.isArray(item?.likes)
          ? item.likes
          : [];

      const alreadyLiked =
        likes.includes(
          user.uid
        );

      if (
        item?.videoUrl &&
        !isCloudinaryUrl(
          item.videoUrl
        )
      ) {
        await toggleVideoLike(
          item.id,
          user.uid,
          alreadyLiked
        );
      } else {
        await toggleLike(
          item.id,
          user.uid,
          alreadyLiked
        );
      }

      await refreshFeed();
    } catch (error) {
      console.error(
        "Like error:",
        error
      );

      alert(
        "Unable to update like."
      );
    }
  };

  /* =========================================================
     HANDLE SHARE
  ========================================================= */

  const handleShare = async (
    item
  ) => {
    const postLink =
      `${window.location.origin}/posts?post=${item.id}`;

    const shareText =
      `📍 ${item?.area || "Eyal App"}

${item?.text || "Check this update"}

👇 Open in Eyal App`;

    if (
      navigator.share
    ) {
      try {
        await navigator.share({
          title: "Eyal App",
          text: shareText,
          url: postLink,
        });

        return;
      } catch (error) {
        if (
          error?.name ===
          "AbortError"
        ) {
          return;
        }

        console.error(
          "Native share failed:",
          error
        );
      }
    }

    const whatsappText =
      `${shareText}\n\n${postLink}`;

    window.open(
      `https://wa.me/?text=${encodeURIComponent(
        whatsappText
      )}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="feed-wrap">

 {/* ===================================================
    AREA UPDATES TITLE
=================================================== */}

<div className="area-updates-title">
  Post your area updates...
</div>

<br />
<br />
{/* ===================================================
    CLEAN COMPOSER
=================================================== */}

{user ? (
  <div className="composer">

    {/* TOP ROW — Avatar + Input */}
    <div className="composer-top-row">

      {userPhoto ? (
        <img
          src={userPhoto}
          className="composer-avatar"
          alt="Profile"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <FaUserCircle className="composer-avatar-icon" />
      )}

      <input
        type="text"
        className="composer-input"
        placeholder="Share your update..."
        value={text}
        onChange={(event) =>
          setText(event.target.value)
        }
      />

    </div>

    {/* BOTTOM ROW — Actions + Post */}
    <div className="composer-bottom-row">

      <div className="composer-actions">

        {/* IMAGE */}
        <label
          className="composer-attach"
          title="Add image"
        >
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={(event) =>
              handleFileChange(event, "image")
            }
          />

          <AiOutlineCamera size={20} />
        </label>

        {/* VIDEO */}
        <label
          className="composer-attach"
          title="Add video"
        >
          <input
            hidden
            type="file"
            accept="video/*"
            onChange={(event) =>
              handleFileChange(event, "video")
            }
          />

          <AiOutlineVideoCamera size={20} />
        </label>

        {/* LINK */}
        <button
          type="button"
          className="composer-attach"
          title="Attach URL"
          onClick={openAttachUrlModal}
        >
          <AiOutlineLink size={20} />
        </button>

        {/* LOCATION */}
        <button
          type="button"
          className="composer-location"
          title="Choose location"
          onClick={() =>
            setShowLocationPicker(true)
          }
        >
          <AiOutlineEnvironment size={20} />
        </button>

      </div>

      {/* POST BUTTON */}
      <button
        type="button"
        className="composer-post"
        onClick={handlePost}
        disabled={posting}
      >
        {posting ? "Posting..." : "Post"}
      </button>

    </div>

  </div>
) : (
  <div className="signin-composer">
    <button
      type="button"
      className="composer-post"
      onClick={() => navigate("/login")}
    >
      Sign In to Post
    </button>
  </div>
)}

      {/* ===================================================
          FILE PREVIEW
      =================================================== */}

      {preview && (
        <div className="image-preview-box">
          {fileType === "video" ? (
            <video
              src={preview}
              controls
              className="image-preview"
            />
          ) : (
            <img
              src={preview}
              alt="Preview"
              className="image-preview"
            />
          )}

          <button
            type="button"
            className="remove-preview-btn"
            onClick={
              removePreview
            }
          >
            <AiOutlineCloseCircle
              size={22}
            />
          </button>
        </div>
      )}

      {/* ===================================================
          LOCATION PICKER
      =================================================== */}

      {showLocationPicker && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            zIndex: 1000,
          }}
          onClick={() =>
            setShowLocationPicker(
              false
            )
          }
        >
          <div
            style={{
              background:
                "#fff",
              borderRadius: 16,
              padding: 20,
              width: "80%",
              maxWidth: 320,
            }}
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <h4
              style={{
                textAlign:
                  "center",
                marginBottom: 10,
              }}
            >
              Choose your post location
            </h4>

            <div
              style={{
                display:
                  "flex",
                flexDirection:
                  "column",
                gap: 10,
              }}
            >
              {AREAS.map(
                (area) => (
                  <label
                    key={area}
                    style={{
                      display:
                        "flex",
                      alignItems:
                        "center",
                      padding:
                        "10px 12px",
                      borderRadius: 12,
                      border:
                        selectedArea ===
                        area
                          ? "2px solid #2563eb"
                          : "1px solid #e5e7eb",
                      background:
                        selectedArea ===
                        area
                          ? "#eff6ff"
                          : "#fff",
                      cursor:
                        "pointer",
                      fontWeight: 500,
                    }}
                  >
                    <input
                      type="radio"
                      value={area}
                      checked={
                        selectedArea ===
                        area
                      }
                      onChange={(
                        event
                      ) =>
                        setSelectedArea(
                          event.target.value
                        )
                      }
                      style={{
                        marginRight: 10,
                      }}
                    />

                    {area ===
                    "All"
                      ? "🌍"
                      : "📍"}{" "}
                    {area}
                  </label>
                )
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                if (
                  !selectedArea
                ) {
                  alert(
                    "Select a location"
                  );
                  return;
                }

                setShowLocationPicker(
                  false
                );

                if (
                  text.trim() ||
                  file ||
                  urlLink
                ) {
                  handlePost();
                }
              }}
              style={{
                width: "100%",
                marginTop: 14,
                padding: 12,
                borderRadius: 12,
                border: "none",
                background:
                  "linear-gradient(135deg,#2563eb,#1d4ed8)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 15,
                cursor: "pointer",
              }}
            >
              Confirm Location
            </button>
          </div>
        </div>
      )}

      {/* ===================================================
          AREA FILTER CHIPS
      =================================================== */}

      <div className="chip-row">
        <button
          type="button"
          className={`chip ${
            activeArea ===
            "All updates"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setActiveArea(
              "All updates"
            )
          }
        >
          All updates
        </button>

        {AREAS.filter(
          (area) => area !== "All"
        ).map((area) => (
          <button
            type="button"
            key={area}
            className={`chip ${
              activeArea ===
              area
                ? "active"
                : ""
            }`}
            onClick={() =>
              setActiveArea(
                area
              )
            }
          >
            {area}
          </button>
        ))}
      </div>

      {/* ===================================================
          FEED
      =================================================== */}

      <div className="feed-list">
        {loadingFeed && (
          <p
            style={{
              textAlign:
                "center",
              color:
                "#6b7280",
              marginTop: 20,
            }}
          >
            Loading... Please wait!
          </p>
        )}

        {!loadingFeed &&
          filteredFeed.length === 0 && (
            <p
              style={{
                textAlign:
                  "center",
                color:
                  "#6b7280",
                marginTop: 20,
              }}
            >
              No updates available.
            </p>
          )}

        {filteredFeed.map(
          (item) => {
            const likes =
              Array.isArray(
                item?.likes
              )
                ? item.likes
                : [];

            const liked =
              user &&
              likes.includes(
                user.uid
              );

            const hasFirebaseVideo =
              item?.videoUrl &&
              !isCloudinaryUrl(
                item.videoUrl
              );

            return (
              <React.Fragment
                key={item.id}
              >
                <Helmet>
                  <title>
                    {item?.text ||
                      "Eyal App"}
                  </title>

                  <meta
                    property="og:title"
                    content={
                      item?.text ||
                      "Eyal App"
                    }
                  />

                  <meta
                    property="og:description"
                    content={
                      item?.area ||
                      ""
                    }
                  />

                  <meta
                    property="og:image"
                    content={
                      item?.imageUrl ||
                      ""
                    }
                  />

                  <meta
                    property="og:type"
                    content="article"
                  />

                  <meta
                    property="og:url"
                    content={`https://eyalmart.com/posts?post=${item.id}`}
                  />

                  <meta
                    name="twitter:card"
                    content="summary_large_image"
                  />
                </Helmet>

                <article
                  id={`post-${item.id}`}
                  className="feed-card"
                >
                  {/* POST HEADER */}

                  <header
                    className="feed-head"
                    style={{
                      cursor:
                        "pointer",
                    }}
                    onClick={() =>
                      navigate(
                        `/products?uid=${item.userId}`
                      )
                    }
                  >
                    {item?.userPhotoURL ? (
                      <img
                        src={
                          item.userPhotoURL
                        }
                        className="feed-avatar"
                        alt="User"
                        onError={(
                          event
                        ) => {
                          event.currentTarget.style.display =
                            "none";
                        }}
                      />
                    ) : (
                      <FaUserCircle className="feed-avatar-icon" />
                    )}

                    <div>
                      <h4>
                        {item?.userName ||
                          "User"}
                      </h4>

                      <small>
                        {item?.area ||
                          "Unknown area"}{" "}
                        •{" "}
                        {item?.createdAt
                          ? new Date(
                              item.createdAt
                            ).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )
                          : ""}
                      </small>
                    </div>
                  </header>

                  {/* POST TEXT */}

                  {item?.text && (
                    <p className="feed-text">
                      {item.text}
                    </p>
                  )}

                  {/* ===================================================
    IMAGE — CLICK TO VIEW FULL ORIGINAL
=================================================== */}

{item?.imageUrl && (
  <img
    src={item.imageUrl}
    className="feed-image"
    alt="Post"
    loading="lazy"
    onClick={() =>
      openImageViewer(item.imageUrl)
    }
  />
)}

                  {/* FIREBASE VIDEO ONLY */}

                  {hasFirebaseVideo && (
                    <div className="video-wrapper">
                      <video
                        key={
                          item.videoUrl
                        }
                        src={
                          item.videoUrl
                        }
                        className="video-player"
                        controls
                        loop
                        playsInline
                        preload="metadata"
                        onError={(
                          event
                        ) => {
                          console.error(
                            "Firebase video loading failed:",
                            event.currentTarget.error
                          );
                        }}
                      />
                    </div>
                  )}

                  {/* OLD CLOUDINARY VIDEO MESSAGE */}

                  {item?.videoUrl &&
                    isCloudinaryUrl(
                      item.videoUrl
                    ) && (
                      <div
                        style={{
                          padding:
                            "16px",
                          background:
                            "#fff7ed",
                          color:
                            "#9a3412",
                          borderRadius:
                            12,
                          fontSize: 14,
                          marginTop: 10,
                        }}
                      >
                        This old video is no
                        longer available.
                        Please upload the video
                        again.
                      </div>
                    )}

                  {/* URL PREVIEW */}

                  {item?.urlLink && (
                    <div
                      className="url-preview-card"
                      onClick={() =>
                        openUrlPreviewModal(
                          item.urlLink
                        )
                      }
                    >
                      <div className="url-preview-icon">
                        {getHostname(
                          item.urlLink
                        )
                          .charAt(0)
                          .toUpperCase() ||
                          "🔗"}
                      </div>

                      <div className="url-preview-text">
                        <div className="url-preview-link read-more">
                          Read More →
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ACTIONS */}

                  <footer className="feed-actions">
                    <button
                      type="button"
                      className={`like-btn ${
                        liked
                          ? "liked"
                          : ""
                      }`}
                      onClick={() =>
                        handleLike(
                          item
                        )
                      }
                    >
                      ❤️{" "}
                      {likes.length ||
                        ""}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleShare(
                          item
                        )
                      }
                    >
                      ↗ Share
                    </button>
                  </footer>
                </article>
              </React.Fragment>
            );
          }
        )}
      </div>

      {/* ===================================================
          ATTACH URL MODAL
      =================================================== */}

      {showAttachUrlModal && (
        <div
          className="url-modal-overlay"
          onClick={() =>
            setShowAttachUrlModal(
              false
            )
          }
        >
          <div
            className="url-modal-box"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="url-modal-header">
              <h3>
                Attach URL
              </h3>

              <button
                type="button"
                className="url-modal-close-btn"
                onClick={() =>
                  setShowAttachUrlModal(
                    false
                  )
                }
              >
                Close
              </button>
            </div>

            <input
              value={tempUrlInput}
              onChange={(event) =>
                setTempUrlInput(
                  event.target.value
                )
              }
              placeholder="Paste URL (https://...)"
              className="url-modal-input"
            />

            <div className="url-modal-actions">
              <button
                type="button"
                className="url-btn url-btn-primary"
                onClick={
                  attachUrl
                }
              >
                Attach
              </button>

              <button
                type="button"
                className="url-btn url-btn-light"
                onClick={() =>
                  setTempUrlInput(
                    urlLink
                  )
                }
              >
                Reset
              </button>

              <button
                type="button"
                className="url-btn url-btn-light"
                onClick={
                  clearUrl
                }
              >
                Clear
              </button>
            </div>

            <p className="url-modal-note">
              Link opens inside the app
              in a modal. If blocked,
              use Open in new tab.
            </p>
          </div>
        </div>
      )}

      {/* ===================================================
          URL PREVIEW MODAL
      =================================================== */}

      {showUrlPreviewModal && (
        <div
          className="url-viewer-overlay"
          onClick={
            closeUrlPreviewModal
          }
        >
          <div
            className="url-viewer-box"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="url-viewer-header">
              <div className="url-viewer-title">
                {currentModalUrl
                  ? getHostname(
                      currentModalUrl
                    )
                  : "Preview"}
              </div>

              <div
                style={{
                  display:
                    "flex",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      currentModalUrl,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  Open in new tab
                </button>

                <button
                  type="button"
                  onClick={
                    closeUrlPreviewModal
                  }
                >
                  Close
                </button>
              </div>
            </div>

            <div className="url-viewer-body">
              <iframe
                src={
                  currentModalUrl
                }
                title="External link preview"
                sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
              />
            </div>
          </div>
        </div>
      )}

      {/* ===================================================
    FULL IMAGE VIEWER MODAL
=================================================== */}

{showImageViewer && selectedImage && (
  <div
    className="image-viewer-overlay"
    onClick={closeImageViewer}
  >
    <div
      className="image-viewer-box"
      onClick={(event) =>
        event.stopPropagation()
      }
    >
      {/* CLOSE BUTTON */}

      <button
        type="button"
        className="image-viewer-close"
        onClick={closeImageViewer}
        aria-label="Close image viewer"
      >
        ✕
      </button>

      {/* ORIGINAL FULL IMAGE */}

      <img
        src={selectedImage}
        alt="Full size post"
        className="image-viewer-full"
      />
    </div>
  </div>
)}
    </div>
  );
}